import { useCallback, useMemo, useState } from 'react'

import { useDeleteAffiliatedCompany } from 'commons/hooks/bank/affiliatedCompany'
import { FilterCompanyAffiliatedCompaniesWebPage } from 'commons/service/bank/companyDetails'
import { Toast } from 'ui/components'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

import { MappedAffiliatedCompany } from '../MappedAffiliatedCompany'
import { useAffiliatedCompanyManage } from './useAffiliatedCompanyManage'

export const useComponentEssence = (
  companyId: number,
  companyName: string,
  filterCompanyAffiliatedCompaniesWebPage: (filter: FilterCompanyAffiliatedCompaniesWebPage) => void,
  affiliatedCompaniesTotalCount: number
) => {
  const [loading, setLoading] = useState(false)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: companyId,
      page: 1,
      size: 10
    }),
    []
  )

  const [tableFilter, setTableFilter] = useState<{
    relationshipId: number
    page: number
    size: number
  }>(defaultFilter)

  const changePagination = useCallback(
    async (page: number, size: number) => {
      setLoading(true)

      const filter = { ...tableFilter, page, size }

      setTableFilter(filter)

      await filterCompanyAffiliatedCompaniesWebPage(filter)

      setLoading(false)
    },
    [filterCompanyAffiliatedCompaniesWebPage, tableFilter]
  )

  const getCompanyAffiliatedCompaniesAfterDeleteWebPage = () => {
    setLoading(true)

    const isDeletingAnnualReviewLastOnPage = tableFilter.size * tableFilter.page >= affiliatedCompaniesTotalCount

    const page = isDeletingAnnualReviewLastOnPage
      ? Math.ceil((affiliatedCompaniesTotalCount - 1) / tableFilter.size) || 1
      : tableFilter.page

    filterCompanyAffiliatedCompaniesWebPage({
      ...tableFilter,
      page
    })

    const filter = { ...tableFilter, page }

    setTableFilter(filter)

    setLoading(false)
  }

  const [affCompanyToDelete, setAffCompanyToDelete] = useState<MappedAffiliatedCompany | undefined>(undefined)

  const { routine: deleteHandler } = useDeleteAffiliatedCompany({
    onSuccess: async () => {
      getCompanyAffiliatedCompaniesAfterDeleteWebPage()
      Toast.successSmall(
        `Affiliation with "${affCompanyToDelete?.affiliatedCompanyName}" company successfully deleted.`
      )
    },
    onError: (error) => handlerServerError(error)
  })

  const deleteAffiliatedCompany = useCallback(
    () => (affCompanyToDelete?.id ? deleteHandler(affCompanyToDelete?.id) : null),
    [affCompanyToDelete, deleteHandler]
  )

  const { Confirmation: DeleteConfirmation, open } = useConfirmation({
    message: `Are you sure you want to delete affiliation with "${
      affCompanyToDelete?.affiliatedCompanyName || 'this'
    }" company?`,
    onConfirm: deleteAffiliatedCompany,
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const openDeleteConfirmation = useCallback(
    (item: MappedAffiliatedCompany) => {
      setAffCompanyToDelete(item)
      open()
    },
    [open, setAffCompanyToDelete]
  )

  const manageData = useAffiliatedCompanyManage(companyId, companyName, () =>
    filterCompanyAffiliatedCompaniesWebPage(tableFilter)
  )

  return {
    loading,
    tableFilter,
    changePagination,
    DeleteConfirmation,
    openDeleteConfirmation,
    manageData
  }
}
